/**
 * planet-3rdgen — THE planet renderer (ported from labs/planet-3rdgen, branch
 * volcano). Builds a displaced hex-sphere mesh entirely from { seed, type,
 * size } via the shared pipeline (hybrid/planet-terrain.js): Gaussian-blended
 * per-hex heights, game-recipe mountain plugs fused into massifs, Gipfelzug
 * crest ridges, per-fragment biome atlas with zone-tinted mountains and edge
 * fades, emissive lava, per-seed gas giants, per-type atmosphere halo.
 *
 * Geometry is built in the lab's world units (NOISE_RADIUS sphere) and the
 * returned group is scaled to the game's planet radius — every calibrated
 * constant stays valid verbatim.
 *
 * The game hexasphere uses Y as the polar axis (latitude bands, gas bands),
 * matching Three.js' scene up/spin axis.
 * Lighting: MeshStandardMaterial — lit by the sun's PointLight (sun-shader.js).
 */

import {
	AdditiveBlending,
	BackSide,
	BufferGeometry,
	FrontSide,
	Float32BufferAttribute,
	Group,
	LinearFilter,
	LinearMipmapLinearFilter,
	LineBasicMaterial,
	LineSegments,
	Mesh,
	MeshLambertMaterial,
	NoColorSpace,
	ShaderMaterial,
	SphereGeometry,
	TextureLoader,
	Uint8BufferAttribute,
	Vector3,
} from 'three';
import { getSphereMath } from '../terrain/hexasphere.js';
import { generateTerrain, gasPalette, PLANET_TYPES, Noise, NOISE_RADIUS } from '../terrain/planet-terrain.js';

const RADIUS = NOISE_RADIUS; // lab world units; group is scaled to game size
// mesh tessellation per fan triangle (finer = resolves ridges). Triangle count
// scales with SUB²: 4 → ~311k tris/planet (desktop), 2 → ~78k (mobile XR —
// standalone headsets can't feed 1.9M tris/system to a mobile GPU, and the
// JS mesh build on their CPU dominates startup). Override for testing:
// globalThis.PLANET_SUB = n before planets are built.
const IS_MOBILE_GPU = typeof navigator !== 'undefined' && /Android|Mobile|Pico|Quest|OculusBrowser/i.test(navigator.userAgent);
// mobile 3 (not 2): the height field is sampled AT vertices — at SUB=2 the
// interpolated water line drifts visibly off the hex borders (Pico screenshot)
const SUB_DEFAULT = IS_MOBILE_GPU ? 3 : 4;
const RIDGE = 1.2; // mountain ridge/valley strength (× maxLand), 0 = smooth domes
const RISE = RADIUS * 0.013; // height gained per hex-step inland
const RELIEF = 0.22; // vertical exaggeration of the GEOMETRY only
const BLEND = 0.0225; // how tightly terrain hugs the hex borders
const MTN = 10; // mountain height, the user-calibrated standard
const CREST_W = 3800; // reach of the Gipfelzug ridge (world units)
const maxLand = 4 * RISE; // height that maps to snow
const volH = RADIUS * 0.0045 * MTN;

let atlas = null;
function getAtlas(url) {
	if (atlas) return atlas;
	const src = url || '/assets/gfx/planet/shader-assets/terrain.jpg';
	atlas = new TextureLoader().load(src,
		t => console.log('planet-3rdgen: atlas loaded', t.image?.width + 'x' + t.image?.height),
		undefined,
		() => console.error('planet-3rdgen: atlas FAILED to load:', src));
	atlas.colorSpace = NoColorSpace;
	// mipmaps + anisotropy: without them the atlas aliases/shimmers badly in
	// XR ("pixeled"). The 3% UV inset in atlasSample keeps cell bleed at bay.
	atlas.generateMipmaps = true;
	atlas.minFilter = LinearMipmapLinearFilter;
	atlas.magFilter = LinearFilter;
	atlas.anisotropy = 8;
	return atlas;
}

// buildPlanetMesh(planet, opts?) → { group, planetMesh, atmosphere, faceIndex,
// buildGrid() } — group is scaled to the game's radius (ceil(size/5))
export function buildPlanetMesh(planet, opts = {}) {
	const seed = planet.seed ?? planet.name ?? 'Miserit';
	const type = PLANET_TYPES[planet.type] ? planet.type : 'EARTHLIKE';
	const PT = { ...PLANET_TYPES[type], ...(PLANET_TYPES[type].gas ? gasPalette(seed) : null) };
	const sphere = getSphereMath(planet.type, planet.size);
	const { centers, boundaries, neighbors: nbr } = sphere;
	const N = centers.length;
	const terr = generateTerrain(sphere, { seed, type });

	// normalized tile centres
	const cx = new Float32Array(N), cy = new Float32Array(N), cz = new Float32Array(N);
	for (let i = 0; i < N; i++) {
		const c = centers[i];
		const m = Math.hypot(c[0], c[1], c[2]) || 1;
		cx[i] = c[0] / m; cy[i] = c[1] / m; cz[i] = c[2] / m;
	}

	// per tile, per boundary edge → the neighbour tile across that edge (-1 if none)
	const ck = c => c[0] + ',' + c[1] + ',' + c[2];
	const ekey = (a, b) => (a <= b ? a + '|' + b : b + '|' + a);
	const edgeMap = new Map();
	for (let t = 0; t < N; t++) {
		const cs = boundaries[t];
		for (let i = 0; i < cs.length; i++) {
			const k = ekey(ck(cs[i]), ck(cs[(i + 1) % cs.length]));
			let arr = edgeMap.get(k); if (!arr) edgeMap.set(k, (arr = [])); arr.push(t);
		}
	}
	const edgeNbr = [];
	for (let t = 0; t < N; t++) {
		const cs = boundaries[t];
		edgeNbr.push([]);
		for (let i = 0; i < cs.length; i++) {
			const arr = edgeMap.get(ekey(ck(cs[i]), ck(cs[(i + 1) % cs.length])));
			edgeNbr[t].push(arr[0] === t ? (arr[1] ?? -1) : arr[0]);
		}
	}

	// tile data: cells/tints from the pipeline, PATCHES applied (terraform/events)
	const bcell = new Float32Array(N);
	const zoneId = new Uint8Array(N);
	const tileH = new Float32Array(N);
	const isMtn = new Uint8Array(N);
	for (let i = 0; i < N; i++) {
		bcell[i] = terr.cell[i];
		zoneId[i] = terr.zoneId[i];
		tileH[i] = terr.baseH[i];
		isMtn[i] = terr.isMtnTile[i];
	}
	if (planet.patches) {
		for (const k in planet.patches) {
			const i = Number(k), p = planet.patches[k];
			if (p.t !== undefined) { bcell[i] = p.t; isMtn[i] = p.t === 9 || p.t === 12 ? 1 : 0; }
			// patch heights come in gameplay scale (-1..1); approximate steps
			if (p.h !== undefined) tileH[i] = p.h <= 0 ? -p.h * -4 : (p.h - 0.05) * 10;
		}
	}

	// GIPFELZÜGE: every connected mountain cluster gets a crest path — the graph
	// diameter (double BFS). The ridge function is CLUSTER-GLOBAL: every tile
	// of a massif references the SAME full path and bead axis, so the ridge
	// height is identical on both sides of internal borders — per-tile nearest
	// segments + per-tile axes caused rifts at hex borders (light leaked in).
	const crestPath = new Array(N).fill(null); // per tile → cluster path (flat xyz)
	const crestAxis = new Array(N).fill(null); // per tile → cluster bead axis
	{
		const seen = new Uint8Array(N);
		const bfsFar = (start, allowed) => {
			const prev = new Map([[start, -1]]);
			const order = [start];
			let far = start;
			for (let h = 0; h < order.length; h++) {
				const t = order[h]; far = t;
				for (const nb of nbr[t]) if (allowed.has(nb) && !prev.has(nb)) { prev.set(nb, t); order.push(nb); }
			}
			return { far, prev };
		};
		for (let i = 0; i < N; i++) {
			if (!isMtn[i] || seen[i]) continue;
			const tiles = [i]; seen[i] = 1;
			for (let h = 0; h < tiles.length; h++) for (const nb of nbr[tiles[h]]) if (isMtn[nb] && !seen[nb]) { seen[nb] = 1; tiles.push(nb); }
			const allowed = new Set(tiles);
			const a = bfsFar(tiles[0], allowed).far;
			const { far: b, prev } = bfsFar(a, allowed);
			const path = [];
			for (let t = b; t !== -1; t = prev.get(t)) path.push(t);
			const flat = new Float32Array(path.length * 3);
			path.forEach((p, j) => { flat[j * 3] = cx[p]; flat[j * 3 + 1] = cy[p]; flat[j * 3 + 2] = cz[p]; });
			let ax = [flat[flat.length - 3] - flat[0], flat[flat.length - 2] - flat[1], flat[flat.length - 1] - flat[2]];
			const al = Math.hypot(ax[0], ax[1], ax[2]);
			ax = al > 1e-6 ? [ax[0] / al, ax[1] / al, ax[2] / al] : [0, 0, 1];
			for (const t of tiles) { crestPath[t] = flat; crestAxis[t] = ax; }
		}
	}

	// smooth blend of per-hex heights → continuous height field.
	// ALL grid-relative constants were calibrated in the lab on ONE planet size
	// (18). Tile spacing on the unit sphere scales with 1/divisions, so BLEND,
	// crest reach, bead wavelength and the gather grid must scale with the
	// planet — otherwise big planets get wandering coasts (Anthar, size 20)
	// and small ones blocky coasts or even holes in the height gather.
	const DIV = planet.type === 'GAS' ? Math.ceil(planet.size / 5) : Math.ceil(planet.size);
	const SP = 18 / DIV; // spacing scale relative to the lab calibration
	const blend = BLEND * SP;
	const crestW = CREST_W * SP;
	const beadLen = 2200 * SP;
	const G = DIV, CELL = 2 / G, gkey = (a, b, c) => (a * 64 + b) * 64 + c;
	const grid = new Map();
	for (let i = 0; i < N; i++) {
		const k = gkey(Math.floor((cx[i] + 1) / CELL), Math.floor((cy[i] + 1) / CELL), Math.floor((cz[i] + 1) / CELL));
		let arr = grid.get(k); if (!arr) grid.set(k, (arr = [])); arr.push(i);
	}
	const twoSig2 = 2 * blend * blend;
	const _grad = new Vector3(); // gradient of the macro height, filled by heightAt
	function heightAt(ux, uy, uz) {
		const ix = Math.floor((ux + 1) / CELL), iy = Math.floor((uy + 1) / CELL), iz = Math.floor((uz + 1) / CELL);
		let w = 0, h = 0, ax = 0, ay = 0, az = 0, bx = 0, by = 0, bz = 0;
		for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) for (let dk = -1; dk <= 1; dk++) {
			const arr = grid.get(gkey(ix + di, iy + dj, iz + dk)); if (!arr) continue;
			for (const i of arr) {
				const dx = ux - cx[i], dy = uy - cy[i], dz = uz - cz[i];
				const ww = Math.exp(-(dx * dx + dy * dy + dz * dz) / twoSig2);
				const wt = ww * tileH[i];
				w += ww; h += wt;
				ax += ww * dx; ay += ww * dy; az += ww * dz;
				bx += dx * wt; by += dy * wt; bz += dz * wt;
			}
		}
		if (w === 0) { _grad.set(0, 0, 0); return 0; }
		const hb = h / w;
		const k = RISE / (blend * blend * w);
		_grad.set((hb * ax - bx) * k, (hb * ay - by) * k, (hb * az - bz) * k);
		let hh = hb * RISE;
		if (hh > 0) { // ridges only on elevated land (fine detail, not in the normal)
			const m = Math.min(1, Math.max(0, (hh / maxLand - 0.2) / 0.6));
			if (m > 0) hh += (ridged(ux * RADIUS, uy * RADIUS, uz * RADIUS) - 0.55) * (RIDGE * maxLand) * m;
		}
		return hh;
	}

	// ridged multifractal — mountain ridges + branching valleys, world-space
	const ridgeNoise = new Noise({ seed: seed + '-ridge', octaves: 1, height: 1, scale: RADIUS / 4 });
	function ridged(wx, wy, wz) {
		let sum = 0, amp = 1, freq = 1, weight = 1;
		for (let o = 0; o < 4; o++) {
			let n = ridgeNoise.get(wx * freq, wy * freq, wz * freq);
			n = 1 - Math.abs(n);
			n *= n;
			n *= weight;
			weight = Math.min(1, n * 2);
			sum += n * amp;
			amp *= 0.5;
			freq *= 2;
		}
		return sum; // ~0..1.7
	}

	// tessellate the hex-sphere into a displaced INDEXED mesh
	const SUB = globalThis.PLANET_SUB || opts.sub || SUB_DEFAULT;
	const positions = [], normals = [], elev = [], uvs = [], proxE = [], proxW = [], nbrE = [], nbrW = [], owns = [], indices = [];
	const faceIndex = []; // per-triangle tile index (click raycasts)
	let curScale = 0.04, curOwnCell = 0, curTile = 0;
	const _p = new Vector3(), _nrm = new Vector3();
	const _cN = new Vector3(), _t1 = new Vector3(), _t2 = new Vector3(), _off = new Vector3();
	const _eN = [0, 1, 2, 3, 4, 5].map(() => new Vector3());
	const _eD = new Float32Array(6);
	const _eC = new Float32Array(6);
	const _eOut = new Uint8Array(6);
	const rowStart = [];
	{ let s = 0; for (let i = 0; i <= SUB; i++) { rowStart[i] = s; s += (SUB - i + 1); } }
	let _cpath = null, _cax = null; // current tile's CLUSTER crest (shared by whole massif)
	function segDist(px, py, pz, ax, ay, az, bx, by, bz) {
		const abx = bx - ax, aby = by - ay, abz = bz - az;
		const apx = px - ax, apy = py - ay, apz = pz - az;
		const ab2 = abx * abx + aby * aby + abz * abz;
		let t = ab2 > 0 ? (apx * abx + apy * aby + apz * abz) / ab2 : 0;
		t = t < 0 ? 0 : t > 1 ? 1 : t;
		const dx = apx - abx * t, dy = apy - aby * t, dz = apz - abz * t;
		return Math.sqrt(dx * dx + dy * dy + dz * dz);
	}
	function ridgeAt(x, y, z) {
		if (!_cpath) return 0;
		let d;
		if (_cpath.length === 3) { // single-tile cluster: centre peak
			const dx = x - _cpath[0], dy = y - _cpath[1], dz = z - _cpath[2];
			d = Math.sqrt(dx * dx + dy * dy + dz * dz);
		} else {
			d = Infinity;
			for (let s = 0; s + 5 < _cpath.length; s += 3) {
				const sd = segDist(x, y, z, _cpath[s], _cpath[s + 1], _cpath[s + 2], _cpath[s + 3], _cpath[s + 4], _cpath[s + 5]);
				if (sd < d) d = sd;
			}
		}
		const cp = Math.max(0, 1 - (d * RADIUS) / crestW);
		if (cp === 0) return 0;
		const s = (x * _cax[0] + y * _cax[1] + z * _cax[2]) * RADIUS;
		const bead = 0.72 + 0.48 * Math.abs(Math.sin(s / beadLen));
		return volH * 0.7 * Math.pow(cp, 1.4) * bead;
	}
	function vertAt(dx, dy, dz) {
		const inv = 1 / Math.hypot(dx, dy, dz);
		const ux = dx * inv, uy = dy * inv, uz = dz * inv;
		const pr = i => { const p = 1 - (ux * _eN[i].x + uy * _eN[i].y + uz * _eN[i].z) / _eD[i]; return p < 0 ? 0 : p > 1 ? 1 : p; };
		const p0 = pr(0), p1 = pr(1), p2 = pr(2), p3 = pr(3), p4 = pr(4), p5 = pr(5);
		let h = heightAt(ux, uy, uz); // also fills _grad
		// LITTLE VOLCANO, the game's recipe: mountain tiles lift their interior
		// onto a flat-topped cone; skirt only toward non-mountain tiles → massifs
		if (isMtn[curTile]) {
			const pv = [p0, p1, p2, p3, p4, p5];
			let mp = 0, mi = 0;
			for (let i = 0; i < 6; i++) if (_eOut[i] && pv[i] > mp) { mp = pv[i]; mi = i; }
			const q = Math.min(1, Math.max(0, (mp - 0.5) / 0.45));
			const w = 1 - q * q * (3 - 2 * q);
			const du = 0.004 * SP;
			const r0 = ridgeAt(ux, uy, uz);
			const r1 = ridgeAt(ux + _t1.x * du, uy + _t1.y * du, uz + _t1.z * du);
			const r2 = ridgeAt(ux + _t2.x * du, uy + _t2.y * du, uz + _t2.z * du);
			h += (volH + r0) * w;
			_grad.addScaledVector(_eN[mi], (volH + r0) * (6 * q * (1 - q)) / 0.45 / _eD[mi])
				.addScaledVector(_t1, w * (r1 - r0) / du)
				.addScaledVector(_t2, w * (r2 - r0) / du);
		}
		const e = h * RELIEF;
		_p.set(ux, uy, uz).multiplyScalar(RADIUS + e);
		positions.push(_p.x, _p.y, _p.z);
		const gx = _grad.x * RELIEF, gy = _grad.y * RELIEF, gz = _grad.z * RELIEF;
		const kk = RADIUS + e + (gx * ux + gy * uy + gz * uz);
		_nrm.set(ux * kk - gx, uy * kk - gy, uz * kk - gz).normalize();
		normals.push(_nrm.x, _nrm.y, _nrm.z);
		elev.push(h);
		_off.set(ux - _cN.x, uy - _cN.y, uz - _cN.z);
		uvs.push(_off.dot(_t1) / curScale * 0.5 + 0.5, _off.dot(_t2) / curScale * 0.5 + 0.5);
		// prox: normalized Uint8 (0..1 range). nbr/own: raw Uint8 cell ids, own
		// zone folded in (cell + zone*16, max 12+3*16=60) — the OWN zone tint
		// used to be its own vec3 attribute (aTint); zoneTint() already exists
		// in-shader for neighbour fades, so decoding it for OWN costs nothing
		// extra and removes a whole 12-byte-per-vertex attribute for free.
		proxE.push(p0 * 255, p1 * 255, p2 * 255); proxW.push(p3 * 255, p4 * 255, p5 * 255);
		nbrE.push(_eC[0], _eC[1], _eC[2]); nbrW.push(_eC[3], _eC[4], _eC[5]);
		owns.push(curOwnCell + zoneId[curTile] * 16);
	}
	const C = new Vector3(), A = new Vector3(), B = new Vector3();
	function emitFan(C, A, B) {
		const base = positions.length / 3;
		for (let i = 0; i <= SUB; i++) {
			for (let j = 0; j <= SUB - i; j++) {
				const u = i / SUB, v = j / SUB, w = 1 - u - v;
				vertAt(C.x * w + A.x * u + B.x * v, C.y * w + A.y * u + B.y * v, C.z * w + A.z * u + B.z * v);
			}
		}
		for (let i = 0; i < SUB; i++) {
			for (let j = 0; j < SUB - i; j++) {
				// CCW winding as seen from OUTSIDE — required for FrontSide rendering
				// and correct (non-flipped) normals in the fragment shader
				const a = base + rowStart[i] + j, b = base + rowStart[i + 1] + j, c = base + rowStart[i] + j + 1;
				indices.push(a, c, b); faceIndex.push(curTile);
				if (j < SUB - i - 1) { indices.push(b, c, base + rowStart[i + 1] + j + 1); faceIndex.push(curTile); }
			}
		}
	}
	for (let t = 0; t < N; t++) {
		C.set(cx[t], cy[t], cz[t]);
		_cN.set(cx[t], cy[t], cz[t]);
		_t1.set(Math.abs(_cN.y) > 0.9 ? 1 : 0, Math.abs(_cN.y) > 0.9 ? 0 : 1, 0).cross(_cN).normalize();
		_t2.crossVectors(_cN, _t1).normalize();
		curOwnCell = bcell[t];
		curTile = t;
		_cpath = crestPath[t];
		_cax = crestAxis[t];
		const cs = boundaries[t], n = cs.length;
		curScale = _off.set(+cs[0][0], +cs[0][1], +cs[0][2]).normalize().distanceTo(_cN) || 0.04;
		for (let i = 0; i < 6; i++) { _eN[i].copy(_cN); _eD[i] = 1; _eC[i] = curOwnCell; _eOut[i] = 0; }
		for (let i = 0; i < n; i++) {
			A.set(+cs[i][0], +cs[i][1], +cs[i][2]).normalize();
			B.set(+cs[(i + 1) % n][0], +cs[(i + 1) % n][1], +cs[(i + 1) % n][2]).normalize();
			_eN[i].crossVectors(A, B).normalize();
			if (_eN[i].dot(_cN) < 0) _eN[i].negate();
			_eD[i] = _eN[i].dot(_cN) || 1;
			const nb = edgeNbr[t][i];
			// pack neighbour zone with its cell (cell + zone*16) so edge fades
			// carry the MOUNTAIN's zone tint, not the receiving tile's
			_eC[i] = (nb >= 0 && terr.land[nb]) ? bcell[nb] + zoneId[nb] * 16 : curOwnCell + zoneId[t] * 16;
			_eOut[i] = (nb < 0 || !isMtn[nb]) ? 1 : 0;
		}
		for (let i = 0; i < n; i++) {
			A.set(+cs[i][0], +cs[i][1], +cs[i][2]).normalize();
			B.set(+cs[(i + 1) % n][0], +cs[(i + 1) % n][1], +cs[(i + 1) % n][2]).normalize();
			emitFan(C, A, B);
		}
	}

	let mE = 0; for (let k = 0; k < elev.length; k++) if (elev[k] > mE) mE = elev[k];
	const uSnow = { value: PT.snow };
	const uMaxH = { value: mE || 1 };

	// GPU vertex bandwidth matters a lot on mobile XR — pack what used to be
	// five Float32 attributes (aProxE/W, aNbrE/W, aOwn: 52 bytes) plus a whole
	// separate aTint vec3 (12 bytes) into Uint8s (13 bytes total, aTint gone):
	// prox is a 0..1 blend factor (normalized Uint8 loses nothing visible);
	// nbr/own are small integer ids (raw Uint8, exact, own now carries its
	// zone packed in too). ~64 bytes/vertex removed.
	const geom = new BufferGeometry();
	geom.setAttribute('position', new Float32BufferAttribute(positions, 3));
	geom.setAttribute('normal', new Float32BufferAttribute(normals, 3));
	geom.setAttribute('aElev', new Float32BufferAttribute(elev, 1));
	geom.setAttribute('aUV', new Float32BufferAttribute(uvs, 2));
	geom.setAttribute('aProxE', new Uint8BufferAttribute(proxE, 3, true));
	geom.setAttribute('aProxW', new Uint8BufferAttribute(proxW, 3, true));
	geom.setAttribute('aNbrE', new Uint8BufferAttribute(nbrE, 3, false));
	geom.setAttribute('aNbrW', new Uint8BufferAttribute(nbrW, 3, false));
	geom.setAttribute('aOwn', new Uint8BufferAttribute(owns, 1, false));
	geom.setIndex(indices);

	// colour per pixel from the smoothly-interpolated height → crisp coast at
	// any zoom; deep sea darkens with depth; lava glows via emissive
	// FrontSide: the planet is a closed surface — halves fragment work (XR!).
	// Lambert instead of Standard: diffuse-only lighting — a matte planet gains
	// nothing from the GGX specular pipeline, XR pays it per eye.
	const material = new MeshLambertMaterial({ side: FrontSide });
	// three keys its program cache on onBeforeCompile's SOURCE — identical for
	// every planet, so without this all planets would reuse the first-compiled
	// shader (gas giants rendered with the terrain shader). Gas palettes are
	// seed-derived, so gas keys include the seed.
	material.customProgramCacheKey = () => 'planet3g|' + type + (PT.gas ? '|' + seed : '');
	material.onBeforeCompile = shader => {
		shader.uniforms.atlas = { value: getAtlas(opts.atlasUrl) };
		shader.uniforms.uSnow = uSnow;
		shader.uniforms.uMaxH = uMaxH;
		shader.vertexShader = shader.vertexShader
			.replace('#include <common>', '#include <common>\nattribute float aElev;\nattribute vec2 aUV;\nattribute vec3 aProxE;\nattribute vec3 aProxW;\nattribute vec3 aNbrE;\nattribute vec3 aNbrW;\nattribute float aOwn;\nvarying float vElev;\nvarying vec2 vHexUV;\nvarying vec3 vProxE;\nvarying vec3 vProxW;\nvarying vec3 vNbrE;\nvarying vec3 vNbrW;\nvarying float vOwn;\nvarying vec3 vWPos;')
			.replace('#include <begin_vertex>', '#include <begin_vertex>\nvElev = aElev;\nvHexUV = aUV;\nvProxE = aProxE;\nvProxW = aProxW;\nvNbrE = aNbrE;\nvNbrW = aNbrW;\nvOwn = aOwn;\nvWPos = position;');
		shader.fragmentShader = shader.fragmentShader
			.replace('#include <common>', '#include <common>\nuniform sampler2D atlas;\nuniform float uSnow;\nuniform float uMaxH;\nvarying float vElev;\nvarying vec2 vHexUV;\nvarying vec3 vProxE;\nvarying vec3 vProxW;\nvarying vec3 vNbrE;\nvarying vec3 vNbrW;\nvarying float vOwn;\nvarying vec3 vWPos;\nvec3 g_emc = vec3(0.0);\nvec3 atlasSample(float cell, vec2 uv){ cell=floor(cell+0.5); float cols=4.0,rows=4.0; float cx=mod(cell,cols),cy=floor(cell/cols); vec2 iuv=uv*0.94+0.03; vec2 a=vec2(cx/cols+iuv.x/cols, 1.0-(cy/rows+(1.0-iuv.y)/rows)); return texture2D(atlas,a).rgb; }\nvec3 zoneTint(float z){ return z < 0.5 ? vec3(1.0) : z < 1.5 ? vec3(1.55,1.62,1.72) : z < 2.5 ? vec3(1.5,1.3,0.98) : z < 3.5 ? vec3(1.14,1.1,1.02) : vec3(1.15,0.5,0.32); }\nfloat h2(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }\nfloat vn(vec2 p){ vec2 i=floor(p),f=fract(p); f=f*f*(3.0-2.0*f); float a=h2(i),b=h2(i+vec2(1.0,0.0)),c=h2(i+vec2(0.0,1.0)),d=h2(i+vec2(1.0,1.0)); return mix(mix(a,b,f.x),mix(c,d,f.x),f.y); }')
			.replace('#include <color_fragment>', `#include <color_fragment>
			{
				float h = vElev;
				float shore = ${(RADIUS * 0.002).toFixed(1)};
				vec3 terr;
				if (h < -shore) {
					terr = mix(vec3(${PT.shallow}), vec3(${PT.deep}), clamp((-h - shore)/5333.3, 0.0, 1.0));
				} else if (h < 0.0) {
					terr = vec3(${PT.beach});
				} else {
					// own cell+zone are packed into one byte (cell + zone*16) —
					// same scheme as the neighbour packing below, decoded once
					float ownZone = floor(vOwn / 16.0 + 0.001);
					float ownCell = vOwn - ownZone * 16.0;
					vec2 uv = clamp(vHexUV, 0.0, 1.0);
					terr = atlasSample(ownCell, uv);
					float m = -1.0; // fade mask, computed lazily — most pixels never fade
					float prox[6]; float ncl[6];
					prox[0]=vProxE.x; prox[1]=vProxE.y; prox[2]=vProxE.z; prox[3]=vProxW.x; prox[4]=vProxW.y; prox[5]=vProxW.z;
					ncl[0]=vNbrE.x; ncl[1]=vNbrE.y; ncl[2]=vNbrE.z; ncl[3]=vNbrW.x; ncl[4]=vNbrW.y; ncl[5]=vNbrW.z;
					for (int i = 0; i < 6; i++) {
						// fade band starts at 0.72 — tile-interior pixels skip the
						// decode + atlas fetch entirely (XR fragment cost)
						if (prox[i] <= 0.70) continue;
						if (m < 0.0) m = vn(vHexUV * 5.0);
						float zn = floor(ncl[i] / 16.0 + 0.001);
						float cell = ncl[i] - zn * 16.0;
						if (cell > ownCell + 0.5) {
							float w = smoothstep(0.72 + m * 0.16, 1.0, prox[i]);
							vec3 nsamp = atlasSample(cell, uv);
							if (cell > 8.5 && cell < 9.5) nsamp = min(vec3(1.0), nsamp * zoneTint(zn));
							terr = mix(terr, nsamp, w);
						}
					}
					float t = clamp(h / max(uMaxH, 1.0), 0.0, 1.0);
					if (ownCell > 8.5 && ownCell < 9.5) terr = min(vec3(1.0), terr * zoneTint(ownZone));
					terr = mix(terr, vec3(0.949, 0.961, 1.0), smoothstep(uSnow, uSnow + 0.08, t));
					${PT.gas ? `
					// GAS planet: painted bands (Y is the polar axis in the game)
					vec3 gp = normalize(vWPos);
					float glat = asin(clamp(gp.y, -1.0, 1.0));
					float glon = atan(gp.z, gp.x);
					float wob = vn(vec2(glat * 6.0, glon * 2.0)) * 1.6;
					float band = sin(glat * ${(PT.bandFreq || 16).toFixed(1)} + wob) * 0.5 + 0.5;
					float storm = smoothstep(0.75, 0.95, vn(vec2(glat * 9.0 + 5.0, glon * 3.0)));
					terr = mix(mix(vec3(${PT.g1}), vec3(${PT.g2}), band), vec3(${PT.g3}), storm * 0.6);
					` : ''}
				}
				diffuseColor.rgb = pow(terr, vec3(2.2));
				// LAVA GLOWS: strongly red-dominant terrain self-illuminates
				g_emc = diffuseColor.rgb * smoothstep(0.10, 0.35, terr.r - terr.g * 1.5) * 2.0;
			}
			`)
			.replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += g_emc;');
	};

	const planetMesh = new Mesh(geom, material);

	// atmosphere halo — per-type colour, Fresnel rim, additive. A smooth glow
	// with no silhouette detail needs no high tessellation (was 64x64 = 8192
	// tris; every planet in view gets this + the core sphere below, on top of
	// the terrain mesh — a needless multiplier across the whole system).
	const atmosphere = new Mesh(
		new SphereGeometry(RADIUS, 24, 24),
		new ShaderMaterial({
			vertexShader: `
				varying vec3 vertexNormal;
				void main() {
					vertexNormal = normalize(normalMatrix * normal);
					gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 0.9);
				}
			`,
			fragmentShader: `
				varying vec3 vertexNormal;
				void main() {
					float intensity = pow(max(0.0, 0.5 - dot(vertexNormal, vec3(0.0, 0.0, 1.0))), 1.9);
					gl_FragColor = vec4(vec3(${PT.atmo}) * intensity, 1.0);
				}
			`,
			blending: AdditiveBlending,
			side: BackSide,
			transparent: true,
			depthWrite: false,
		}),
	);
	atmosphere.scale.set(1.0125, 1.0125, 1.0125);

	// hex-tile boundaries draped on the height field, in MESH-LOCAL units.
	// Each boundary point is shared by 3 tiles — dedupe the (expensive)
	// heightAt gathers via a point cache. Serves BOTH the hover outlines
	// (sector-map parents them into the scaled planetMesh) and the grid.
	const drapedBoundaries = [];
	const drapedCenters = [];
	{
		const d = new Vector3();
		const pointCache = new Map();
		const drape = c => {
			const k = c[0] + ',' + c[1] + ',' + c[2];
			let p = pointCache.get(k);
			if (!p) {
				d.set(+c[0], +c[1], +c[2]).normalize();
				const h = heightAt(d.x, d.y, d.z);
				const r = RADIUS + Math.max(h, 0) * RELIEF + RADIUS * 0.0012;
				pointCache.set(k, (p = [d.x * r, d.y * r, d.z * r]));
			}
			return p;
		};
		for (const cs of boundaries) drapedBoundaries.push(cs.map(drape));
		for (const c of centers) drapedCenters.push(drape(c));
	}

	// hex grid — built from the SAME draped points (no extra height pass)
	function buildGrid() {
		const pos = [];
		for (const tile of drapedBoundaries) for (let i = 0; i < tile.length; i++) {
			pos.push(...tile[i], ...tile[(i + 1) % tile.length]);
		}
		const g = new BufferGeometry();
		g.setAttribute('position', new Float32BufferAttribute(pos, 3));
		return new LineSegments(g, new LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35 }));
	}

	function buildAxisLine() {
		const g = new BufferGeometry();
		const r = RADIUS * 1.22;
		g.setAttribute('position', new Float32BufferAttribute([0, -r, 0, 0, r, 0], 3));
		return new LineSegments(g, new LineBasicMaterial({
			color: 0x888888,
			transparent: true,
			opacity: 0.85,
			depthTest: false,
		}));
	}

	// opaque dark core just below the deepest ocean floor — with FrontSide any
	// hairline crack at a tile border would show the sky/atmosphere through the
	// planet; this backstops them invisibly. Never seen up close (it's under
	// the terrain), so it can be coarse.
	const core = new Mesh(
		new SphereGeometry(RADIUS * 0.95, 12, 12),
		new MeshLambertMaterial({ color: 0x05070d }),
	);

	// group scaled to the game's planet radius — all children come along
	const group = new Group();
	group.add(planetMesh);
	group.add(atmosphere);
	group.add(core);
	group.add(buildAxisLine());
	const gameRadius = Math.ceil(planet.size / 5);
	group.scale.setScalar(gameRadius / RADIUS);

	return {
		group,
		planetMesh,
		atmosphere,
		faceIndex,
		buildGrid,
		centers,
		drapedCenters,
		boundaries: drapedBoundaries,
		neighbors: nbr,
		uniforms: { uSnow, uMaxH },
	};
}
